import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { brandsApi } from '@/features/brands/api';
import { queryKeys } from '@/lib/api/queryKeys';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { useT } from '@/lib/i18n/useT';
import { ProjectsTab } from './ProjectsTab';

/**
 * Standalone Projects list for a single brand.
 *
 * Renders the SAME surface as the brand profile's "Projects" tab, but on its
 * own page — so the sidebar's "Projects" entry shows ONLY the projects grid
 * (+ create dialog), without the brand hero, KPIs or sibling tabs.
 * Brand-scoped via the `:id` route param.
 */
export function BrandProjectsPage() {
  const t = useT();
  const { id } = useParams<{ id: string }>();
  const brandId = Number(id);
  const validId = Number.isFinite(brandId) && brandId > 0;

  const brandQ = useQuery({
    queryKey: queryKeys.brands.detail(brandId),
    queryFn: () => brandsApi.get(brandId),
    enabled: validId,
  });
  const brand = brandQ.data ?? null;

  return (
    <div className="page">
      <Breadcrumbs
        className="mb-4"
        items={[
          { label: brand?.name ?? t('breadcrumbs.brand'), to: `/brand/${brandId}` },
          { label: t('sidebar.projects') },
        ]}
      />
      <ProjectsTab
        brandId={validId ? brandId : 0}
        brandPrimary={brand?.backgroundColor ?? null}
        brandAccent={brand?.secondaryColor ?? null}
      />
    </div>
  );
}
